import { CLASS_EDIT_PERIOD_WEEKS } from "@app/utils/config";
import { ddMMyyyyFormat, ddMMyyyyHHmmFormat, displayAddress, fromIsoDate, getSundayXWeeksAgo } from "@app/utils/functions";
import { Address } from "./Address";
import { BaseEntity } from "./BaseEntity";
import { ClientExecutive } from "./ClientExecutive";
import { Country } from "./Country";
import { Course } from "./Course";
import { CourseVersion } from "./CourseVersion";
import {
    ADHOC_REQUESTER, CLIENT_DELIVERY, CurrencyCode, DELIVERY_RESPONSIBILITY, KLASS_DECISION,
    KLASS_HARDWARE, KLASS_MODALITY, KLASS_STATUS, KLASS_TYPE, PAYMENT_MODE, REGION, REQUEST_STATUS
} from "./Enums";
import { Facility } from "./Facility";
import { Flag } from "./Flag";
import { Instructor } from "./Instructor";
import { Language } from "./Language";
import { PrivateCourse } from "./PrivateCourse";
import { TrainingSpecialistStrategic } from "./TrainingSpecialistStrategic";

export interface Klass extends BaseEntity {
    offeringId: number;
    instructors: Instructor[];
    allocatedInstructors: Instructor[];  // editable
    finalInstructors: Instructor[];      // editable
    tss: TrainingSpecialistStrategic; // editable
    facility: Facility;               
    language: Language;
    country: Country;
    type: KLASS_TYPE;
    modality: KLASS_MODALITY;
    status: KLASS_STATUS;
    /**
     * The Red Hat operating unit the class was sold out of. Stored by the service rather than
     * looked up from `country`, which answers where the class is delivered - see REGION. Absent
     * on a class the Classes Outlook report named no unit for, RHLS classes among them.
     */
    region: REGION;
    /**
     * Who delivers the class. On every class since the Classes Outlook import began sending it;
     * it was an ad hoc field before that, and an ad hoc class still carries it here.
     */
    deliveryResponsibility: DELIVERY_RESPONSIBILITY;
    course: Course;
    version: CourseVersion;
    startDateTime: Date;
    endDateTime: Date;
    /**
     * How many days this class was actually delivered over, entered by hand when the dates cannot say.
     *
     * Null on a standard delivery, which is nearly all of them: a class runs N consecutive working days
     * where N is what the course is long, and startDateTime to endDateTime says so. This is for the ones
     * that do not - a five day course taught over eight weeks, a boot camp on alternate weeks - where the
     * span measures the calendar and not the teaching. `isNonStandardDelivery` in model/DeliveryPattern
     * is the test for that, and is the same one the service's `Non standard delivery pattern` flag asks,
     * which clears itself once this is set.
     *
     * A decimal, because half days are real: an RHLS session is three hours and counts half a day.
     *
     * The only field on a class a human owns - the two imports write every other one - so it is audited,
     * and `/history` answers who set it and when.
     */
    deliveredDays: number | null;
    /**
     * Which days the class was actually delivered on, as "yyyy-MM-dd", ordered by date.
     *
     * Optional, and `deliveredDays` is usually enough. This is for what a count cannot answer: a delivery
     * running from December into January with ten taught days somewhere inside it is ten days of
     * nobody-knows-which-month, and monthly utilization is the whole question. Where these are present
     * the service treats them as authoritative and does not consult the count.
     *
     * A set of dates and not a pattern: "alternate Tuesdays" is a description of a schedule, and what the
     * counting needs is the days themselves.
     */
    deliveryDays: string[];
    payingDelegates: number;
    rhlsPremiumDelegates: number;
    rhlsCourseDelegates: number;
    trainingUnitsDelegates: number;
    freeDelegates: number;
    internalDelegates: number;
    bundledDelegates: number;
    delegatesBooked: number;
    totalEnrolled: number;
    maxDelegates: number;
    /**
     * What the class was sold in, as an ISO 4217 code. On every class since the Classes Outlook
     * import began sending one; it was a private-class field before that, and a private class
     * still carries it here.
     */
    currency: CurrencyCode;
    /** What the class sold for, in `currency`. */
    revenue: number;
    /**
     * The same amount before tax, which only the LMS reports: the private-class sheet has no
     * before-tax figure, so a private class carries one only once the LMS knows about it. Absent
     * as well on a class sold in more than one currency - no single amount would represent it, so
     * the total lands in `revenue` and this is left off rather than mixing currencies.
     */
    revenueBeforeTax: number;
    comments: string;
    goNogoDate: Date;
    hardware: KLASS_HARDWARE;
    vtLabsRequired: boolean;
    virtualLabVersions: string[];
    /**
     * Whether the class is taught as an RHLS live virtual class.
     *
     * A different question from `modality === RHLS_PREMIUM`, which is who it was sold to - the
     * subscription rather than a seat. The two are independent: a premium class can be taught in
     * a room, and an ordinary one can be an RHLS live virtual class.
     */
    rhlsLiveClass: boolean;
    facilityConfirmed: boolean;
    languageChanged: boolean;
    languageChangeRequest: boolean;
    guarantedToRun: false;
    published: boolean;
    decision: KLASS_DECISION;
    released: boolean;
    releaseConfirmed: boolean;
    domain: string;
    cancelledReason: string;
    clientExecutive: ClientExecutive;
    clientDelivery: CLIENT_DELIVERY;
    /** The class's own city, as the source system sent it. Independent of the facility's address. */
    city: string;
    dateCreated: Date;
    /**
     * When the *offering* was raised in the LMS, which is not `dateCreated` - that is when this
     * application's service first stored the class, and is generated there. The two sit together
     * under names a reader has to be careful with.
     *
     * Sent without a zone, unlike every other date on a class: the report writes it as a wall
     * clock reading and nothing says which zone it was read in. Render it with
     * `ddMMyyyyHHmmFormat` and not the `Tz` variant, which would append the browser's zone to a
     * reading that was never in it, and do not compare it with `startDateTime`.
     */
    offeringCreated: Date;
    activeFlags: Flag[];
}

export interface OpenEnrolmentKlass extends Klass {
    type: KLASS_TYPE.OPEN_ENROLMENT;
}

/**
 * `currency` and `revenue` are inherited rather than declared here. They were private-class fields
 * until the Classes Outlook import began sending both for every class; what a class sold for is
 * not a thing only a private one has, and the private-class sheet goes on writing the same two.
 */
export interface PrivateKlass extends Klass {
    type: KLASS_TYPE.PRIVATE;
    privateCourse: PrivateCourse
    daysSold: number;
    customerName: string;
    requestId: string;
    customerLocation: boolean;
    deliveryAddressText: string;
    deliveryAddress: Address;
    englishOption: boolean;
    scopeCallRequestId: string;
    /** Where the scoping paperwork for this class is kept, as the source system recorded it - normally a link. */
    scopingFolder: string;
    /** The RHN logins of the delegates attending, as the source system sent them: plain ids, not resolved to anything here. */
    participantsRHNID: string[];
    rhscId: string;
    ticketOpenDate: Date;
    ticketCloseDate: Date;
    paymentMode: PAYMENT_MODE;
    rushFee: boolean;
    facilityFee: boolean;
    cancellationFee: boolean;
    preparationFee: boolean;
    spocName: string;
    spocEmail: string;
    spocPhone: string;
    spocConnected: boolean;
    spocResponse: string;
    facilityContactName: string;
    facilityContactEmail: string;
    facilityContactPhone: string;
}

/**
 * An open enrolment class that was raised as a request rather than scheduled, and that carries the
 * paperwork of that request alongside the class itself: the ticket, the justification, who asked
 * and who approved.
 *
 * A subtype of OpenEnrolmentKlass because once approved it is one - published, enrolled onto and
 * delivered like any other - so it turns up in the Classes list and its detail page like the rest.
 * `requestId` (the `ADRnnnn` form) ties it back to the PipelineKlass it was approved from.
 *
 * The near misses against inherited fields are deliberate, and not duplicates:
 * - `declinedReason` is why the *request* was turned down; `cancelledReason` is why a class that
 *   already existed was called off.
 * - `delegatesInterested` is the headcount the request asked for, which is a claim rather than a
 *   booking - the payingDelegates/totalEnrolled family is what the class actually sold.
 * - `enrolmentsProcessed` is how many of those interested delegates have been put through
 *   enrolment: bookkeeping on the request, not a delegate count on the class.
 * - `ticketOpenDate` has a namesake on PrivateKlass. Different type, and neither is inherited.
 *
 * `deliveryResponsibility` is inherited rather than declared here. It was an ad hoc field until the
 * Classes Outlook import began sending it for every class, and it reads the same on both: the
 * request names the party that will deliver, the LMS names the party that does.
 */
export interface AdHocKlass extends OpenEnrolmentKlass {
    /** `ADRnnnn`. The id the ad hoc system holds, and what the import upserts on. */
    requestId: string;
    ticketOpenDate: Date;
    customerName: string;
    delegatesInterested: number;
    paymentOrderAvailable: boolean;
    justification: string;
    specialRequests: string;
    requestStatus: REQUEST_STATUS;
    declinedReason: string;
    tssApproval: REQUEST_STATUS;
    resourceBookingDate: Date;
    niitCaseId: string;
    enrolmentsProcessed: number;
    requestType: string;
    /** Not validated as an email by the service, deliberately: a bad address must not stall the import. */
    secondaryNotificationEmail: string;
    requester: ADHOC_REQUESTER;
    openAllocation: boolean;
}

/** The instructor lists a class carries, in the order they supersede one another. */
export const KLASS_INSTRUCTOR_FIELDS = ['instructors', 'allocatedInstructors', 'finalInstructors'] as const;

export type KlassInstructorField = typeof KLASS_INSTRUCTOR_FIELDS[number];

/**
 * The names of a class's instructors as a single string, for the places that need plain
 * text rather than markup: exports, sort keys and tooltips. Empty when nobody is assigned,
 * so callers can decide what to show in that case.
 */
export const instructorNames = (instructors: Instructor[] | undefined | null): string =>
    (instructors ?? []).map(i => i?.name).filter(Boolean).join(', ');

/**
 * Who is actually delivering a class: the final line-up once it has been decided, else
 * the allocation, else whoever the source system named. Used wherever a class needs a
 * single answer to "which instructors", such as the calendar and its filters.
 */
export const resolveInstructors = (klass: Klass): Instructor[] => {
    if (klass.finalInstructors?.length) return klass.finalInstructors;
    if (klass.allocatedInstructors?.length) return klass.allocatedInstructors;
    return klass.instructors ?? [];
};

/** How many instructors a class was set up for: the larger of what it came in with and
 * what it was allocated. The number the final line-up is measured against. */
export const expectedInstructorCount = (klass: Klass): number =>
    Math.max(klass.instructors?.length ?? 0, klass.allocatedInstructors?.length ?? 0);

/**
 * Whether a class names fewer final instructors than it was scheduled or allocated for.
 * Releasing in that state silently drops someone from the delivery, so both release paths
 * - the Classes overview modal and the class detail page - warn before going ahead.
 *
 * finalCount overrides the class's own list, for the overview modal where the choice being
 * released is still sitting in the form rather than on the class.
 */
export const hasMissingFinalInstructors = (klass: Klass, finalCount?: number): boolean =>
    (finalCount ?? klass.finalInstructors?.length ?? 0) < expectedInstructorCount(klass);

/**
 * The Sunday that opens the editable window, at local midnight. Classes starting before
 * it are history: they are outside what the store fetches and outside the period in which
 * a class may still be changed.
 */
export const getEditPeriodStart = (): Date => fromIsoDate(getSundayXWeeksAgo(CLASS_EDIT_PERIOD_WEEKS));

/**
 * Whether this class may still be edited. Tested against startDateTime, the same field the
 * store filters its window by, so "in the cache" and "editable" cannot disagree.
 *
 * A class with no start date counts as editable: an incomplete record - an ad hoc class
 * whose date is not agreed yet - is not a historical one, and locking it would leave the
 * field that fixes it unreachable.
 */
export const isKlassEditable = (klass: Klass | undefined | null): boolean => {
    if (!klass?.startDateTime) return true;
    return new Date(klass.startDateTime) >= getEditPeriodStart();
};

/**
 * Whether the import could not read this class's delivery address into an address at all.
 *
 * **`deliveryAddressText` ON ITS OWN MEANS NOTHING OF THE KIND, and it once did.** The import used
 * to write the text only when the parse failed, so its presence was the failure marker and this
 * function would have been `!!klass.deliveryAddressText`. It now writes the text on **every**
 * import, succeeded or not, because a reading can be confidently wrong - one Lisbon class had a
 * house number in its postcode - and the string it started from is the only thing that could ever
 * show it. So the old test now matches every private class that has ever been imported, which is
 * why this exists instead of being written out at each call site.
 *
 * The test that survives the change is the address itself: text was received, and none of the three
 * lines could be filled from it. The city and the country arrive in columns of their own and are
 * written whatever happens to the rest, so they are not part of it.
 *
 * A class this is true of also carries an automation message about it, which
 * KlassAutomationMessages already shows - **prefer that panel** as the place a reader is told, and
 * use this where a per-class badge is genuinely wanted.
 */
export const isDeliveryAddressUnreadable = (klass: Klass | undefined | null): boolean => {
    if (klass?.type !== KLASS_TYPE.PRIVATE) return false;

    const privateKlass = klass as PrivateKlass;
    if (!privateKlass.deliveryAddressText) return false;

    const address = privateKlass.deliveryAddress;
    return !address?.line1 && !address?.line2 && !address?.line3;
};

/**
 * Flattens a Klass into a Map of <column label, primitive value>, resolving every
 * nested entity down to a scalar so the result can be fed straight into a
 * spreadsheet/CSV export. Keys are the labels shown to the reader, matching the
 * wording of the Classes table, so they can be used as headers as they are.
 * The fields specific to each subtype (Private, Ad Hoc) are appended after the
 * common ones, so callers exporting a mixed list should build their column set
 * from the union of the keys of every row.
 */
export const exportKlassData = (klass: Klass) : Map<string, any> => {
    const  _klassMap : Map<string, any> = new Map();

    _klassMap.set("Class Id", klass.id);
    _klassMap.set("Offering Id", klass.offeringId);
    _klassMap.set("Instructor", instructorNames(klass.instructors));
    _klassMap.set("Allocated Instructor", instructorNames(klass.allocatedInstructors));
    _klassMap.set("Final Instructor", instructorNames(klass.finalInstructors));
    _klassMap.set("TSS", klass.tss?.name);
    _klassMap.set("Facility", klass.facility?.name);
    _klassMap.set("Facility Address", klass.facility ? displayAddress(klass.facility.address) : "");
    _klassMap.set("Language", klass.language?.name);
    _klassMap.set("City", klass.city);
    _klassMap.set("Country", klass.country?.name);
    _klassMap.set("Region", klass.region);
    _klassMap.set("Type", klass.type);
    _klassMap.set("Modality", klass.modality);
    _klassMap.set("Status", klass.status);
    _klassMap.set("Delivery Responsibility", klass.deliveryResponsibility);
    _klassMap.set("Course", klass.course?.name);
    _klassMap.set("Course SKU", klass.course?.sku);
    _klassMap.set("Version", klass.version?.version);
    _klassMap.set("Extended SKU", klass.version?.extendedSku);
    _klassMap.set("Start Date", ddMMyyyyHHmmFormat(klass.startDateTime));
    _klassMap.set("End Date", ddMMyyyyHHmmFormat(klass.endDateTime));
    // Both empty on a standard delivery, which is most of them - see Klass.deliveredDays. The dates
    // go out as they are stored, "yyyy-MM-dd", so a spreadsheet can sort them.
    _klassMap.set("Delivered Days", klass.deliveredDays);
    _klassMap.set("Delivery Days", (klass.deliveryDays ?? []).join(", "));
    // No zone on this one, and none written: see Klass.offeringCreated.
    _klassMap.set("Offering Created", ddMMyyyyHHmmFormat(klass.offeringCreated));
    _klassMap.set("Paying Delegates", klass.payingDelegates);
    _klassMap.set("RHLS Premium Delegates", klass.rhlsPremiumDelegates);
    _klassMap.set("RHLS Course Delegates", klass.rhlsCourseDelegates);
    _klassMap.set("Training Units Delegates", klass.trainingUnitsDelegates);
    _klassMap.set("Free Delegates", klass.freeDelegates);
    _klassMap.set("Internal Delegates", klass.internalDelegates);
    _klassMap.set("Bundled Delegates", klass.bundledDelegates);
    _klassMap.set("Delegates Booked", klass.delegatesBooked);
    _klassMap.set("Total Enrolled", klass.totalEnrolled);
    _klassMap.set("Max Delegates", klass.maxDelegates);
    // The code and the raw amounts, not formatted money: a spreadsheet has to be able to add
    // these up, which it cannot do with a symbol in front of them.
    _klassMap.set("Currency", klass.currency);
    _klassMap.set("Revenue", klass.revenue);
    _klassMap.set("Revenue Before Tax", klass.revenueBeforeTax);
    _klassMap.set("Comments", klass.comments);
    _klassMap.set("Go/No-Go Date", ddMMyyyyFormat(klass.goNogoDate));
    _klassMap.set("Hardware", klass.hardware);
    _klassMap.set("VT Labs Required", klass.vtLabsRequired);
    _klassMap.set("Virtual Lab Versions", (klass.virtualLabVersions ?? []).join(", "));
    _klassMap.set("RHLS Live Class", klass.rhlsLiveClass);
    _klassMap.set("Facility Confirmed", klass.facilityConfirmed);
    _klassMap.set("Language Changed", klass.languageChanged);
    _klassMap.set("Language Change Request", klass.languageChangeRequest);
    _klassMap.set("Guaranteed to Run", klass.guarantedToRun);
    _klassMap.set("Published", klass.published);
    _klassMap.set("Decision", klass.decision);
    _klassMap.set("Released", klass.released);
    _klassMap.set("Release Confirmed", klass.releaseConfirmed);
    _klassMap.set("Domain", klass.domain);
    _klassMap.set("Cancelled Reason", klass.cancelledReason);
    _klassMap.set("Client Executive", klass.clientExecutive?.name);
//    _klassMap.set("Flags", klass.activeFlags?.map(flag => flag.flagName).join(", ") ?? "");

    switch (klass.type) {
        case KLASS_TYPE.PRIVATE: {
            const _private = klass as PrivateKlass;
            _klassMap.set("Private Course SKU", _private.privateCourse?.sku);
            _klassMap.set("Private Course Description", _private.privateCourse?.description);
            _klassMap.set("Days Sold", _private.daysSold);
            _klassMap.set("Customer Name", _private.customerName);
            _klassMap.set("Request Id", _private.requestId);
            _klassMap.set("At Customer Location", _private.customerLocation);
            // The header says "as received" because the column no longer means what its old name
            // implied. It was written only when the parse failed, so a value in it was a fault to
            // chase; it is now written on every import, and is the record of what arrived.
            _klassMap.set("Delivery Address (as received)", _private.deliveryAddressText);
            _klassMap.set("Delivery Address", displayAddress(_private.deliveryAddress));
            _klassMap.set("English Option", _private.englishOption);
            _klassMap.set("Scope Call Request Id", _private.scopeCallRequestId);
            _klassMap.set("Scoping Folder", _private.scopingFolder);
            _klassMap.set("Participants RHN Id", (_private.participantsRHNID ?? []).join(", "));
            _klassMap.set("RHSC Id", _private.rhscId);
            _klassMap.set("Ticket Open Date", ddMMyyyyFormat(_private.ticketOpenDate));
            _klassMap.set("Ticket Closed Date", ddMMyyyyFormat(_private.ticketCloseDate));
            _klassMap.set("Payment Mode", _private.paymentMode);
            _klassMap.set("Rush Fee", _private.rushFee);
            _klassMap.set("Facility Fee", _private.facilityFee);
            _klassMap.set("Cancellation Fee", _private.cancellationFee);
            _klassMap.set("Preparation Fee", _private.preparationFee);
            _klassMap.set("SPOC Name", _private.spocName);
            _klassMap.set("SPOC Email", _private.spocEmail);
            _klassMap.set("SPOC Phone", _private.spocPhone);
            _klassMap.set("SPOC Connected", _private.spocConnected);
            _klassMap.set("Facility Contact Name", _private.facilityContactName);
            _klassMap.set("Facility Contact Email", _private.facilityContactEmail);
            _klassMap.set("Facility Contact Phone", _private.facilityContactPhone);
            break;
        }
        case KLASS_TYPE.AD_HOC: {
            const _adHoc = klass as AdHocKlass;
            _klassMap.set("Request Id", _adHoc.requestId);
            _klassMap.set("Request Type", _adHoc.requestType);
            _klassMap.set("Requester", _adHoc.requester);
            _klassMap.set("Request Status", _adHoc.requestStatus);
            _klassMap.set("TSS Approval", _adHoc.tssApproval);
            _klassMap.set("Declined Reason", _adHoc.declinedReason);
            _klassMap.set("Ticket Open Date", ddMMyyyyHHmmFormat(_adHoc.ticketOpenDate));
            _klassMap.set("Customer Name", _adHoc.customerName);
            _klassMap.set("Delegates Interested", _adHoc.delegatesInterested);
            _klassMap.set("Enrolments Processed", _adHoc.enrolmentsProcessed);
            _klassMap.set("Payment Order Available", _adHoc.paymentOrderAvailable);
            _klassMap.set("Open Allocation", _adHoc.openAllocation);
            _klassMap.set("Resource Booking Date", ddMMyyyyFormat(_adHoc.resourceBookingDate));
            _klassMap.set("NIIT Case Id", _adHoc.niitCaseId);
            _klassMap.set("Secondary Notification Email", _adHoc.secondaryNotificationEmail);
            _klassMap.set("Justification", _adHoc.justification);
            _klassMap.set("Special Requests", _adHoc.specialRequests);
            break;
        }
        default:
            break;
    }

    return _klassMap;
}